import { Button, DataTable, DataTablePagination } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { FilterIcon } from 'components/icons'
import SearchBar from 'components/common/dataTable/searchBar'
import { ColDef } from '@ag-grid-community/core'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SyncedPicker, SyncedPickersData } from 'types/pickerMap'
import { getPickersApi } from 'utils/api/pickerEmpMapApi'
import { formatDate } from 'utils/helpers'
import PickerFilterForm, { FILTER_FIELD_PICKERS } from './pickerFilterForm'
import PickerFullSyncButton from './pickerFullSyncButton'

const StatusCell = ({ value }: { value: boolean }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      value ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
    }`}
  >
    {value ? 'Active' : 'Inactive'}
  </span>
)

const dash = ({ value }: { value?: string | null }) => value || '-'

const PICKER_COLUMNS: ColDef<SyncedPicker>[] = [
  { headerName: 'Picker ID', field: 'pickerId', minWidth: 100 },
  {
    headerName: 'Username',
    field: 'username',
    valueFormatter: dash,
    minWidth: 130,
  },
  {
    headerName: 'Name',
    colId: 'name',
    valueGetter: ({ data }) =>
      `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim() || '-',
    minWidth: 160,
  },
  { headerName: 'Emp ID', field: 'empId', valueFormatter: dash },
  {
    headerName: 'Email',
    field: 'email',
    valueFormatter: dash,
    minWidth: 200,
  },
  { headerName: 'Role', field: 'role', valueFormatter: dash },
  {
    headerName: 'Status',
    field: 'isActive',
    cellRenderer: StatusCell,
    minWidth: 110,
  },
  { headerName: 'PIN', field: 'pinId', valueFormatter: dash },
  {
    headerName: 'Old App Store',
    field: 'legacyStore',
    valueFormatter: dash,
    minWidth: 130,
  },
  {
    headerName: 'Picker Names',
    field: 'preparedByNames',
    valueFormatter: ({ value }) => (value?.length ? value.join(', ') : '-'),
    minWidth: 200,
  },
  {
    headerName: 'Last Synced',
    field: 'syncedAt',
    valueFormatter: ({ value }) =>
      value ? formatDate(value, { includeTime: true }) : '-',
    minWidth: 170,
  },
]

// Pickers synced from Order Tracker - view only, filters kept in the URL
const PickerList = () => {
  const [data, setData] = useState<SyncedPickersData>({ docs: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [showFilter, setShowFilter] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const { isEditor } = usePageAccess()

  const { search } = useLocation()
  const { getAllQueries } = useQuery()

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const queries = getAllQueries()
        const filters = queries[FILTER_FIELD_PICKERS] || {}
        const { data: pickers } = await getPickersApi({
          page: queries.page,
          limit: queries.limit,
          search: queries.search,
          role: filters.role,
          isActive: filters.isActive,
          store: filters.store,
        })
        setData(pickers)
      } catch {
        setData({ docs: [] })
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [search, getAllQueries, reloadKey])

  const toggleFilterForm = () => setShowFilter((prev) => !prev)

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="title">Pickers</h3>
          <p className="text-xs text-gray-500">
            View only - synced from Order Tracker.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SearchBar
            placeholder="Search name, username, picker ID, emp ID, email"
            disabled={isLoading || showFilter}
          />
          <Button
            buttonText="Filters"
            className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary ${
              showFilter && 'bg-gray-200'
            } hover:bg-gray-200`}
            variant="custom"
            icon={<FilterIcon />}
            iconClassName="text-dataTableIcon"
            onClick={toggleFilterForm}
            disabled={isLoading}
          />
          {isEditor && (
            <PickerFullSyncButton
              onSynced={() => setReloadKey((key) => key + 1)}
            />
          )}
        </div>
      </div>

      {showFilter ? (
        <PickerFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <>
          <ActiveFilterChip filterKey={FILTER_FIELD_PICKERS} />

          {/* DataTable is h-full, so it needs a parent with a height */}
          <div className="h-[479px]">
            <DataTable
              columns={PICKER_COLUMNS}
              rows={isLoading ? null : data.docs}
              identifier="pickerId"
            />
          </div>

          <DataTablePagination
            currentPage={data?.page}
            totalPages={data?.totalPages}
            hasNextPage={data?.hasNextPage}
            hasPrevPage={data?.hasPrevPage}
            limit={data?.limit}
            nextPage={data?.nextPage}
            prevPage={data?.prevPage}
            disabled={isLoading}
          />
        </>
      )}
    </>
  )
}

export default PickerList
